// NOT-542 — Business Unit control shared by the Job + Estimate forms.
//
// A job/estimate normally INHERITS its Business Unit from the selected job type
// (the "derived" unit). This control is hidden entirely when the tenant has no
// active business units (zero-cost-when-unused), and otherwise runs in one of
// two modes:
//
//   • `override` (default, the Job form) — shows the resolved unit read-only for
//     users WITHOUT `dispatch:reassign`; dispatchers set a MANUAL OVERRIDE via a
//     picker. Picking "None"/reverting to the inherited unit is not an override.
//
//   • `assign` (the Estimate form) — picking the unit of an estimate that has no
//     line items yet is ordinary data entry, not a reassignment of committed
//     work: no permission gate. The caller passes `locked` once the estimate has
//     lines, which freezes the choice to a read-only badge.
import { Label } from '@/components/atoms/label';
import { useHasPermission } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import type { BusinessUnit } from '@/types/business-unit';
import { BusinessUnitSelect } from './BusinessUnitSelect';
import { useBusinessUnitOptions } from './useBusinessUnitOptions';
import {
  RESOLUTION_METHOD_LABELS,
  type BusinessUnitResolutionMethod,
} from '@/lib/ai/scheduling-match';

/**
 * True when `value` is a genuine manual override: a non-null unit that differs
 * from the derived unit (lib/business-units/derivedBusinessUnit — null for a
 * multi-unit job type on create, so any pick is an override). Callers use this
 * to decide whether the override should be sent to the backend.
 */
export function isBusinessUnitOverrideActive(
  value: string | null | undefined,
  derivedBusinessUnitId: string | null | undefined,
): boolean {
  return value != null && value !== (derivedBusinessUnitId ?? null);
}

interface BusinessUnitOverrideFieldProps {
  /** The unit the record carries without an override (null = none yet). */
  derivedBusinessUnitId: string | null;
  /** How many units the selected job type lists — with several and no derived
   *  unit, the server resolves one at save and the field says so. */
  candidateCount?: number;
  /** Current selection: null = inherit from job type, else an explicit unit. */
  value: string | null;
  onChange: (value: string | null) => void;
  /**
   * `override` (default) gates the picker on `dispatch:reassign`; `assign` is an
   * ungated pick. See the file header.
   */
  mode?: 'override' | 'assign';
  /** `assign` mode only — render the resolved unit read-only. */
  locked?: boolean;
  /**
   * Offer only these units — the Business Units of the selected Job Type.
   * Absent/empty is a wildcard (a type listing no units belongs to all of
   * them, and nothing is chosen before a type is picked).
   */
  allowedBusinessUnitIds?: readonly string[];
  /** Applied to the root element (e.g. grid column span in the estimate form). */
  className?: string;
  /** How the derived unit was picked (existing records) — shown as a hint. */
  resolutionMethod?: BusinessUnitResolutionMethod | null;
}

function BusinessUnitBadge({
  unit,
  inherited,
  candidateCount = 0,
}: {
  unit: BusinessUnit | undefined;
  inherited: boolean;
  candidateCount?: number;
}) {
  if (!unit) {
    return (
      <div className="flex min-h-9 items-center rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground">
        {inherited
          ? candidateCount > 1
            ? `Resolved on save from ${candidateCount} job type units`
            : 'Inherited from job type'
          : '—'}
      </div>
    );
  }
  return (
    <div className="flex min-h-9 items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm text-foreground">
      <span
        className="h-2.5 w-2.5 shrink-0 rounded-full border"
        style={{ backgroundColor: unit.color }}
      />
      <span className="truncate">{unit.name}</span>
      {inherited ? <span className="text-xs text-muted-foreground">(inherited)</span> : null}
    </div>
  );
}

export function BusinessUnitOverrideField({
  derivedBusinessUnitId,
  candidateCount = 0,
  resolutionMethod = null,
  value,
  onChange,
  mode = 'override',
  locked = false,
  allowedBusinessUnitIds,
  className,
}: BusinessUnitOverrideFieldProps) {
  const { activeUnits, unitsById } = useBusinessUnitOptions();
  const hasOverridePermission = useHasPermission('dispatch:reassign');
  // In `assign` mode the pick is not a reassignment, so it is not gated on
  // `dispatch:reassign` — only on the caller's own `locked`.
  const canEdit = mode === 'assign' ? !locked : hasOverridePermission;

  // Zero-cost when unused — hide entirely until the tenant defines a unit.
  // Keyed on ACTIVE units: an archived-only tenant still sees nothing.
  if (activeUnits.length === 0) return null;

  const inherited = value == null || value === derivedBusinessUnitId;
  const effectiveId = value ?? derivedBusinessUnitId;
  // Resolved against EVERY unit: a record assigned to a unit that has since
  // been archived must still show that unit's name, not an empty dash.
  const effectiveUnit = effectiveId ? unitsById.get(effectiveId) : undefined;

  if (!canEdit) {
    return (
      <div className={cn('space-y-1.5', className)}>
        <Label>Business unit</Label>
        <BusinessUnitBadge
          unit={effectiveUnit}
          inherited={inherited}
          candidateCount={derivedBusinessUnitId ? 0 : candidateCount}
        />
        {inherited && effectiveUnit && resolutionMethod ? (
          <p className="text-xs text-muted-foreground">
            Picked by {RESOLUTION_METHOD_LABELS[resolutionMethod]}.
          </p>
        ) : null}
        {locked ? (
          <p className="text-xs text-muted-foreground">
            Set before the first line item — it can no longer be changed.
          </p>
        ) : null}
      </div>
    );
  }

  const overrideActive =
    mode === 'override' && isBusinessUnitOverrideActive(value, derivedBusinessUnitId);

  return (
    <div className={cn('space-y-1.5', className)}>
      <Label>Business unit</Label>
      <BusinessUnitSelect
        value={effectiveId}
        onChange={onChange}
        placeholder="Select business unit"
        allowClear={false}
        allowedIds={allowedBusinessUnitIds}
      />
      {inherited && effectiveUnit && resolutionMethod && !overrideActive ? (
        <p className="text-xs text-muted-foreground">
          Picked by {RESOLUTION_METHOD_LABELS[resolutionMethod]}.
        </p>
      ) : null}
    </div>
  );
}
